import Link from 'next/link';
import {
  GradlephantNoHammer,
  MavenShortLogo,
  SbtShortLogo,
  BazelShortLogo,
  NodePackageLogo,
  PythonLogo,
} from '@/components/Logos';
import { roboto_mono } from '@/app/fonts';
import QuickStartLink from './QuickStartLink';
import iphoneBgMaven from './iphonebg-maven.png';
import iphoneBgGradle from './iphonebg-gradle.png';
import iphoneBgSbt from './iphonebg-sbt.png';
import ipadbodyMaven from './ipadbody-maven.png';
import ipadbodySbt from './ipadbody-sbt.png';
import ipadbodyGradle from './ipadbody-gradle.png';
import screenbodyMaven from './screenbody-maven.png';
import screenbodyGradle from './screenbody-gradle.png';
import screenbodySbt from './screenbody-sbt.png';

const bgImages = {
  maven: { sm: iphoneBgMaven, md: ipadbodyMaven, lg: screenbodyMaven },
  gradle: { sm: iphoneBgGradle, md: ipadbodyGradle, lg: screenbodyGradle },
  sbt: { sm: iphoneBgSbt, md: ipadbodySbt, lg: screenbodySbt },
};

export const logoNavItems = [
  {
    label: 'Gradle',
    path: '/scans/gradle',
    supported: true,
    bgImages: bgImages.gradle,
    logoActive: <GradlephantNoHammer monochrome className="size-10 text-white" />,
    logoInactive: <GradlephantNoHammer className="size-10" />,
    activeBgGradient: 'bg-gradient-to-br from-gradle-green to-gradle-blue',
    activeTabBG: 'bg-gradient-to-br from-gradle-green to-gradle-blue [box-shadow:0px_0px_1px_0px_#1B545D_inset]',
    textContent: {
      title: 'Create a Build Scan for Gradle in 30 seconds',
      description: (
        <>
          Try it with a{' '}
          <QuickStartLink href="https://github.com/gradle/gradle-build-scan-quickstart">
            quick-start sample project
          </QuickStartLink>
          , or follow the steps below with your own existing project.
        </>
      ),
      steps: [
        {
          title: 'Step 1',
          description: 'Run your build with --scan and follow the instructions:',
          commandText: 'gradle build --scan',
        },
        {
          title: 'Step 2',
          description:
            'Activate your Build Scan with your email address, then click the link in your email to view it.',
        },
      ],
    },
  },
  {
    label: 'Maven',
    path: '/scans/maven',
    supported: true,
    bgImages: bgImages.maven,
    logoActive: <MavenShortLogo monochrome className="size-10 text-white" />,
    logoInactive: <MavenShortLogo className="size-10" />,
    activeBgGradient: 'bg-gradient-to-b from-[#F28E24] via-[#C72039] to-[#282662]',
    activeTabBG: 'bg-gradient-to-b from-[#F28E24] to-[#C72039] [box-shadow:0px_0px_1px_0px_#7A1727_inset]',
    textContent: {
      title: 'Create a Build Scan for Maven in 30 seconds',
      description: (
        <>
          Try it with a{' '}
          <QuickStartLink href="https://github.com/gradle/maven-build-scan-quickstart">
            quick-start sample project
          </QuickStartLink>
          , or follow the steps below with your own existing project.
        </>
      ),
      steps: [
        {
          title: 'Step 1',
          description: (
            <>
              Add the{' '}
              <Link
                href="https://docs.gradle.com/develocity/maven-extension/current/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4"
              >
                Develocity Extension
              </Link>{' '}
              and run your build.
            </>
          ),
          commandText: 'mvn com.gradle:develocity-maven-extension:2.1:init',
        },
        {
          title: 'Step 2',
          description: 'Run your build',
          commandText: 'mvn install',
        },
        {
          title: 'Step 3',
          description: 'Click the link in your email to view your Build Scan.',
        },
      ],
    },
  },
  {
    label: 'SBT',
    path: '/scans/sbt',
    supported: true,
    bgImages: bgImages.sbt,
    logoActive: <SbtShortLogo monochrome className="size-full text-white" />,
    logoInactive: <SbtShortLogo className="size-full" />,
    activeBgGradient: 'bg-gray-1',
    activeTabBG: 'bg-gradient-to-br from-dark-background to-gray-1 [box-shadow:0px_0px_1px_0px_#000_inset]',
    textContent: {
      title: 'Create a Build Scan for sbt in 30 seconds',
      description: (
        <>
          Try it with a{' '}
          <QuickStartLink href="https://github.com/gradle/sbt-build-scan-quickstart">
            quick-start sample project
          </QuickStartLink>
          , or follow the steps below with your own existing project.
        </>
      ),
      steps: [
        {
          title: 'Step 1',
          description: (
            <>
              Apply the{' '}
              <Link
                href="https://docs.gradle.com/develocity/sbt-plugin/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4"
              >
                Develocity Plugin
              </Link>{' '}
              by adding this code snippet to the <span className={roboto_mono.className}>project/plugins.sbt</span> file
              for your project:
            </>
          ),
          commandText: 'addSbtPlugin("com.gradle" % "sbt-develocity" % "1.3")',
        },
        {
          title: 'Step 2',
          description: 'Run your build',
          commandText: 'sbt package',
        },
        {
          title: 'Step 3',
          description:
            'Activate your Build Scan with your email address, then click the link in your email to view it.',
        },
      ],
    },
  },
  {
    label: 'Bazel',
    path: '/scans/bazel',
    supported: false,
    logoActive: <BazelShortLogo className="size-full text-gray-3 grayscale-[100]" />,
    logoInactive: <BazelShortLogo className="size-full text-gray-3 grayscale-[100]" />,
  },
  {
    label: 'Pip',
    path: '/scans/pip',
    supported: false,
    logoActive: <NodePackageLogo monochrome className="size-10 text-gray-4" />,
    logoInactive: <NodePackageLogo monochrome className="size-10 text-gray-4" />,
  },
  {
    label: 'NPM',
    path: '/scans/npm',
    supported: false,
    logoActive: <PythonLogo monochrome className="size-full text-gray-4" />,
    logoInactive: <PythonLogo monochrome className="size-full text-gray-4" />,
  },
];
